import { useState } from 'react';
import { Dialog } from '../../components/dialog-portal/dialog.tsx';
import { formatDatetime } from '../../utils/fieldFormats.ts';
import { NOT_SPECIFIED_MEASUREMENT_VARIANT } from './useRangeOptions.ts';
import { RangeOptions, RangeSelection } from './useRangeOptions.ts';

type RangeType = RangeSelection['rangeType'];
type QuickRange = 'today' | 'yesterday' | 'last-7-days' | 'this-week' | 'last-week' | 'this-month' | 'last-month';

interface Props {
    style?: React.CSSProperties;
    rangeOptions: RangeOptions | null;
    rangeSelection: RangeSelection | null;
    setRangeSelection: (rangeSelection: RangeSelection) => void;
    onInfoClick?: () => void;
    onClose?: () => void;
}

export function RangeSelect({ style, rangeOptions, rangeSelection, setRangeSelection, onInfoClick, onClose }: Props) {
    // Dialog state - null when closed
    const [dialogType, setDialogType] = useState<Exclude<RangeType, 'All'> | null>(null);

    // Pending values inside the dialog (not yet committed)
    const [pendingBatchIndex, setPendingBatchIndex] = useState<number | null>(null);
    const [pendingOrderNumber, setPendingOrderNumber] = useState<string | null>(null);
    const [pendingOrderSearch, setPendingOrderSearch] = useState('');
    const [pendingDateStart, setPendingDateStart] = useState<Date | null>(null);
    const [pendingDateEnd, setPendingDateEnd] = useState<Date | null>(null);
    const [specifyStartTime, setSpecifyStartTime] = useState(false);
    const [specifyEndTime, setSpecifyEndTime] = useState(false);

    function openDialog(type: Exclude<RangeType, 'All'>) {
        setPendingBatchIndex(rangeSelection!.batchIndex);
        setPendingOrderNumber(rangeSelection!.orderNumber);
        setPendingOrderSearch('');
        const existingStart = rangeSelection!.dateRange.start;
        const existingEnd = rangeSelection!.dateRange.end;
        setPendingDateStart(existingStart);
        setPendingDateEnd(existingEnd);
        setSpecifyStartTime(existingStart != null && (existingStart.getHours() !== 0 || existingStart.getMinutes() !== 0));
        setSpecifyEndTime(existingEnd != null && (existingEnd.getHours() !== 23 || existingEnd.getMinutes() !== 59));
        setDialogType(type);
    }

    function handleDropdownChange(value: RangeType) {
        if (value === 'All') {
            setRangeSelection({ ...rangeSelection!, rangeType: 'All' });
        } else {
            openDialog(value);
        }
    }

    function handleDialogConfirm() {
        if (!dialogType) return;
        const base = { ...rangeSelection! };
        if (dialogType === 'Batch') {
            if (pendingBatchIndex == null) {
                setRangeSelection({ ...base, rangeType: 'All' });
            } else {
                setRangeSelection({ ...base, rangeType: 'Batch', batchIndex: pendingBatchIndex });
            }
        } else if (dialogType === 'Order number') {
            if (!pendingOrderNumber) {
                setRangeSelection({ ...base, rangeType: 'All' });
            } else {
                setRangeSelection({ ...base, rangeType: 'Order number', orderNumber: pendingOrderNumber });
            }
        } else if (dialogType === 'Date') {
            setRangeSelection({ ...base, rangeType: 'Date', dateRange: { start: pendingDateStart, end: pendingDateEnd } });
        }
        setDialogType(null);
    }

    function handleDialogCancel() {
        setDialogType(null);
    }

    function applyQuickRange(range: QuickRange) {
        const now = new Date();
        const start = new Date(now);
        const end = new Date(now);
        const day = now.getDay();
        const mondayOffset = day === 0 ? -6 : 1 - day;

        if (range === 'today') {
            // start/end already initialized to today
        } else if (range === 'yesterday') {
            start.setDate(start.getDate() - 1);
            end.setDate(end.getDate() - 1);
        } else if (range === 'last-7-days') {
            start.setDate(start.getDate() - 6);
        } else if (range === 'this-week') {
            start.setDate(start.getDate() + mondayOffset);
        } else if (range === 'last-week') {
            start.setDate(start.getDate() + mondayOffset - 7);
            end.setDate(end.getDate() + mondayOffset - 1);
        } else if (range === 'this-month') {
            start.setDate(1);
        } else if (range === 'last-month') {
            start.setMonth(start.getMonth() - 1, 1);
            end.setDate(0);
        }

        if (!specifyStartTime) start.setHours(0, 0, 0, 0);
        if (!specifyEndTime) end.setHours(23, 59, 0, 0);

        setPendingDateStart(start);
        setPendingDateEnd(end);
    }

    function toDateLocal(date: Date | null): string {
        if (!date) return '';
        const pad = (v: number) => v.toString().padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    }

    function toHourLocal(date: Date | null): string {
        if (!date) return '';
        const pad = (v: number) => v.toString().padStart(2, '0');
        return pad(date.getHours());
    }

    function toMinuteLocal(date: Date | null): string {
        if (!date) return '';
        const pad = (v: number) => v.toString().padStart(2, '0');
        return pad(date.getMinutes());
    }
    function rangeLabel(): string {
        if (!rangeSelection) return '';
        const { rangeType, batchIndex, orderNumber, dateRange } = rangeSelection;
        if (rangeType === 'All') return `All  (${rangeOptions!.measurementCount})`;
        if (rangeType === 'Batch') {
            const b = rangeOptions!.batches.find((b) => b.index === batchIndex);
            return b ? `${formatDatetime(b.start)}  (${b.measurementCount})` : '-';
        }
        if (rangeType === 'Order number') {
            const o = rangeOptions!.orderNumbers.find((o) => o.orderNumber === orderNumber);
            return o ? `${o.orderNumber}  (${o.measurementCount})` : '-';
        }
        if (rangeType === 'Date') {
            const s = dateRange.start ? formatDatetime(dateRange.start) : '...';
            const e = dateRange.end ? formatDatetime(dateRange.end) : '...';
            if (!dateRange.start && !dateRange.end) return 'all dates';
            return `${s} - ${e}`;
        }
        return '';
    }

    const filteredOrders = rangeOptions?.orderNumbers.filter((o) => o.orderNumber.toLowerCase().includes(pendingOrderSearch.toLowerCase())) ?? [];

    return (
        <>
            <div
                style={{
                    ...style,
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    columnGap: '16px',
                    rowGap: '12px',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <div style={{ gridColumn: 2, display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'center' }}>
                    {rangeOptions && rangeSelection ? (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '0.85em' }}>Data range</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <select value={rangeSelection.rangeType} onChange={(e) => handleDropdownChange(e.target.value as RangeType)}>
                                        {rangeOptions.rangeTypes.map((rt) => (
                                            <option key={rt} value={rt}>
                                                {rt}
                                            </option>
                                        ))}
                                    </select>
                                    {rangeSelection.rangeType !== 'All' && (
                                        <button
                                            style={{ padding: '2px 8px', fontSize: '0.85em' }}
                                            onClick={() => openDialog(rangeSelection.rangeType as Exclude<RangeType, 'All'>)}
                                        >
                                            Edit
                                        </button>
                                    )}
                                    <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.65)' }}>{rangeLabel()}</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '0.85em' }}>Measurement plan</span>
                                <select
                                    value={rangeSelection.measurementVariant ?? ''}
                                    onChange={(e) => setRangeSelection({ ...rangeSelection, measurementVariant: e.target.value || null })}
                                >
                                    <option value=''>All</option>
                                    {rangeOptions.measurementVariants.map((v) => (
                                        <option key={v.name} value={v.name} disabled={!v.available}>
                                            {v.name === NOT_SPECIFIED_MEASUREMENT_VARIANT ? 'Not specified' : v.name}
                                            {!v.available ? ' (none)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : (
                        <span style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.4)' }}>Loading...</span>
                    )}
                </div>

                <div style={{ gridColumn: 3, justifySelf: 'end', display: 'flex', gap: '8px' }}>
                    <button onClick={onInfoClick} title='How to use this page' disabled={!onInfoClick} style={{ minWidth: '32px' }}>
                        ?
                    </button>
                    {onClose && <button onClick={onClose}>Close</button>}
                </div>
            </div>

            {rangeOptions && dialogType === 'Batch' && (
                <Dialog title='Select batch' onClose={handleDialogCancel}>
                    <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {rangeOptions.batches.length === 0 ? (
                            <p>No batches available.</p>
                        ) : (
                            rangeOptions.batches.map((batch) => (
                                <label key={batch.index} style={{ display: 'block', marginBottom: '6px' }}>
                                    <input
                                        type='radio'
                                        name='dlg-batchIndex'
                                        value={batch.index}
                                        checked={batch.index === pendingBatchIndex}
                                        onChange={() => setPendingBatchIndex(batch.index)}
                                        style={{ marginRight: '8px' }}
                                    />
                                    {formatDatetime(batch.start)} ({batch.measurementCount})
                                </label>
                            ))
                        )}
                    </div>
                    <div className='dialog--button-bar'>
                        <button onClick={handleDialogCancel}>Cancel</button>
                        <button onClick={handleDialogConfirm}>Apply</button>
                    </div>
                </Dialog>
            )}

            {rangeOptions && dialogType === 'Order number' && (
                <Dialog title='Select order number' onClose={handleDialogCancel}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: 'min(60vh, 520px)', overflow: 'hidden' }}>
                        <div style={{ marginBottom: '10px', flex: '0 0 auto' }}>
                            <input
                                type='text'
                                placeholder='Search...'
                                value={pendingOrderSearch}
                                onChange={(e) => setPendingOrderSearch(e.target.value)}
                                autoFocus
                                style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
                            {filteredOrders.length === 0 ? (
                                <p>No matches.</p>
                            ) : (
                                filteredOrders.map((order) => (
                                    <label key={order.orderNumber} style={{ display: 'block', marginBottom: '6px' }}>
                                        <input
                                            type='radio'
                                            name='dlg-orderNumber'
                                            value={order.orderNumber}
                                            checked={order.orderNumber === pendingOrderNumber}
                                            onChange={() => setPendingOrderNumber(order.orderNumber)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {order.orderNumber} ({order.measurementCount})
                                    </label>
                                ))
                            )}
                        </div>
                        <div className='dialog--button-bar' style={{ marginTop: '10px', flex: '0 0 auto' }}>
                            <button onClick={handleDialogCancel}>Cancel</button>
                            <button onClick={handleDialogConfirm}>Apply</button>
                        </div>
                    </div>
                </Dialog>
            )}

            {rangeOptions && rangeSelection && dialogType === 'Date' && (
                <Dialog title='Select date range' onClose={handleDialogCancel}>
                    <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ minWidth: '50px' }}>Quick</span>
                        <select
                            defaultValue=''
                            onChange={(e) => {
                                const value = e.target.value as QuickRange | '';
                                if (!value) return;
                                applyQuickRange(value);
                                e.target.value = '';
                            }}
                        >
                            <option value='' disabled>
                                Select quick range...
                            </option>
                            <option value='today'>Today</option>
                            <option value='yesterday'>Yesterday</option>
                            <option value='last-7-days'>Last 7 days</option>
                            <option value='this-week'>This week</option>
                            <option value='last-week'>Last week</option>
                            <option value='this-month'>This month</option>
                            <option value='last-month'>Last month</option>
                        </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr', rowGap: '12px', columnGap: '8px', alignItems: 'center' }}>
                        <span>Start</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <input
                                className='spc-date-input'
                                type='date'
                                value={toDateLocal(pendingDateStart)}
                                max={toDateLocal(pendingDateEnd) || undefined}
                                onChange={(e) => {
                                    if (!e.target.value) {
                                        setPendingDateStart(null);
                                        return;
                                    }
                                    const [y, m, d] = e.target.value.split('-').map(Number);
                                    const date = pendingDateStart ? new Date(pendingDateStart) : new Date();
                                    date.setFullYear(y, m - 1, d);
                                    if (!specifyStartTime) date.setHours(0, 0, 0, 0);
                                    setPendingDateStart(date);
                                }}
                            />
                            <label
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85em', color: '#555', cursor: 'pointer' }}
                            >
                                <input
                                    type='checkbox'
                                    checked={specifyStartTime}
                                    onChange={(e) => {
                                        setSpecifyStartTime(e.target.checked);
                                        if (!e.target.checked && pendingDateStart) {
                                            const d = new Date(pendingDateStart);
                                            d.setHours(0, 0, 0, 0);
                                            setPendingDateStart(d);
                                        }
                                    }}
                                />
                                time
                            </label>
                            {specifyStartTime && pendingDateStart && (
                                <>
                                    <select
                                        value={toHourLocal(pendingDateStart)}
                                        onChange={(e) => {
                                            if (!pendingDateStart) return;
                                            const d = new Date(pendingDateStart);
                                            d.setHours(Number(e.target.value), d.getMinutes(), 0, 0);
                                            setPendingDateStart(d);
                                        }}
                                    >
                                        {Array.from({ length: 24 }, (_, h) => {
                                            const value = h.toString().padStart(2, '0');
                                            return (
                                                <option key={value} value={value}>
                                                    {value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <span>:</span>
                                    <select
                                        value={toMinuteLocal(pendingDateStart)}
                                        onChange={(e) => {
                                            if (!pendingDateStart) return;
                                            const d = new Date(pendingDateStart);
                                            d.setHours(d.getHours(), Number(e.target.value), 0, 0);
                                            setPendingDateStart(d);
                                        }}
                                    >
                                        {Array.from({ length: 60 }, (_, m) => {
                                            const value = m.toString().padStart(2, '0');
                                            return (
                                                <option key={value} value={value}>
                                                    {value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </>
                            )}
                            {pendingDateStart && (
                                <button type='button' onClick={() => setPendingDateStart(null)} title='Clear'>
                                    X
                                </button>
                            )}
                        </div>
                        <span>End</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <input
                                className='spc-date-input'
                                type='date'
                                value={toDateLocal(pendingDateEnd)}
                                min={toDateLocal(pendingDateStart) || undefined}
                                onChange={(e) => {
                                    if (!e.target.value) {
                                        setPendingDateEnd(null);
                                        return;
                                    }
                                    const [y, m, d] = e.target.value.split('-').map(Number);
                                    const date = pendingDateEnd ? new Date(pendingDateEnd) : new Date();
                                    date.setFullYear(y, m - 1, d);
                                    if (!specifyEndTime) date.setHours(23, 59, 0, 0);
                                    setPendingDateEnd(date);
                                }}
                            />
                            <label
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85em', color: '#555', cursor: 'pointer' }}
                            >
                                <input
                                    type='checkbox'
                                    checked={specifyEndTime}
                                    onChange={(e) => {
                                        setSpecifyEndTime(e.target.checked);
                                        if (!e.target.checked && pendingDateEnd) {
                                            const d = new Date(pendingDateEnd);
                                            d.setHours(23, 59, 0, 0);
                                            setPendingDateEnd(d);
                                        }
                                    }}
                                />
                                time
                            </label>
                            {specifyEndTime && pendingDateEnd && (
                                <>
                                    <select
                                        value={toHourLocal(pendingDateEnd)}
                                        onChange={(e) => {
                                            if (!pendingDateEnd) return;
                                            const d = new Date(pendingDateEnd);
                                            d.setHours(Number(e.target.value), d.getMinutes(), 0, 0);
                                            setPendingDateEnd(d);
                                        }}
                                    >
                                        {Array.from({ length: 24 }, (_, h) => {
                                            const value = h.toString().padStart(2, '0');
                                            return (
                                                <option key={value} value={value}>
                                                    {value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <span>:</span>
                                    <select
                                        value={toMinuteLocal(pendingDateEnd)}
                                        onChange={(e) => {
                                            if (!pendingDateEnd) return;
                                            const d = new Date(pendingDateEnd);
                                            d.setHours(d.getHours(), Number(e.target.value), 0, 0);
                                            setPendingDateEnd(d);
                                        }}
                                    >
                                        {Array.from({ length: 60 }, (_, m) => {
                                            const value = m.toString().padStart(2, '0');
                                            return (
                                                <option key={value} value={value}>
                                                    {value}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </>
                            )}

                            {pendingDateEnd && (
                                <button type='button' onClick={() => setPendingDateEnd(null)} title='Clear'>
                                    X
                                </button>
                            )}
                        </div>
                    </div>
                    <div className='dialog--button-bar'>
                        <button onClick={handleDialogCancel}>Cancel</button>
                        <button onClick={handleDialogConfirm}>Apply</button>
                    </div>
                </Dialog>
            )}
        </>
    );
}
